import { ErrorState, LoadingState } from '@playforge/ui';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  List,
  Page,
  Text,
  Toast,
} from '@shopify/polaris';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient, ApiError } from '../lib/api-client';
import { navigateTop } from '../lib/navigate-top';
import { useApi } from '../lib/use-api';
import {
  type ChangePlanResult,
  type CurrentBillingView,
  featureLabel,
  formatLimit,
  type PlanView,
  type SubscriptionPlanId,
} from '../lib/types';

const PLAN_ORDER: SubscriptionPlanId[] = [
  'free',
  'starter',
  'growth',
  'professional',
  'enterprise',
];

function rank(id: SubscriptionPlanId): number {
  return PLAN_ORDER.indexOf(id);
}

export function BillingPage(): JSX.Element {
  const current = useApi(() => apiClient.get<CurrentBillingView>('/billing/current'), []);
  const plansQuery = useApi(() => apiClient.get<PlanView[]>('/billing/plans'), []);

  const [busyPlan, setBusyPlan] = useState<SubscriptionPlanId | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const currentPlanId = current.data?.plan.id;

  // Shopify redirects the merchant back here (embedded) after they approve or
  // decline the charge on the hosted confirmation page. If a subscription is
  // still `pending`, confirm it now so the plan actually applies without the
  // merchant having to do anything else.
  const activatedRef = useRef(false);
  const subscriptionStatus = current.data?.subscriptionStatus;
  useEffect(() => {
    if (subscriptionStatus !== 'pending' || activatedRef.current) return;
    activatedRef.current = true;
    apiClient
      .post('/billing/activate', {})
      .then(() => current.refetch())
      .catch(() => {
        activatedRef.current = false;
      });
  }, [subscriptionStatus, current]);

  const changePlan = useCallback(
    async (target: PlanView, isUpgrade: boolean) => {
      setBusyPlan(target.id);
      setActionError(null);
      try {
        const result = await apiClient.post<ChangePlanResult>(
          isUpgrade ? '/billing/upgrade' : '/billing/downgrade',
          { plan: target.id },
        );
        if (result.confirmationUrl) {
          // Break out of the embedded iframe to Shopify's pricing/approval page.
          navigateTop(result.confirmationUrl);
          return;
        }
        setToast(`Plan changed to ${target.name}`);
        current.refetch();
      } catch (err) {
        setActionError(err instanceof ApiError ? err.message : 'Could not change plan');
      } finally {
        setBusyPlan(null);
      }
    },
    [current],
  );

  if (current.loading || plansQuery.loading) {
    return (
      <Page title="Billing">
        <LoadingState label="Loading billing" />
      </Page>
    );
  }

  if (current.error || plansQuery.error || !current.data || !plansQuery.data) {
    return (
      <Page title="Billing">
        <ErrorState
          message={current.error?.message ?? plansQuery.error?.message ?? 'Failed to load billing'}
          onRetry={() => {
            current.refetch();
            plansQuery.refetch();
          }}
        />
      </Page>
    );
  }

  const currentRank = currentPlanId ? rank(currentPlanId) : -1;
  // With Managed Pricing, Shopify hosts plan selection and rejects any charge
  // the app tries to create, so every plan change goes to the same Shopify page.
  const managedPricing = current.data.managedPricing === true;
  const pricingUrl = current.data.managedPricingUrl ?? null;


  return (
    <Page
      title="Billing"
      subtitle={`Current plan: ${current.data.plan.name}`}
      {...(managedPricing && pricingUrl
        ? {
            // A real link with target="_top" leaves the admin iframe natively,
            // which is more dependable than any scripted navigation.
            primaryAction: {
              content: 'Manage plan on Shopify',
              url: pricingUrl,
              target: '_top' as const,
            },
          }
        : {})}
    >
      <BlockStack gap="500">
        {actionError ? <ErrorState title="Plan change failed" message={actionError} /> : null}

        {managedPricing ? (
          <Banner tone="info" title="Plans are managed by Shopify">
            <p>
              Choose or change your plan on Shopify&apos;s secure pricing page — upgrades,
              downgrades and cancellations all happen there, and your new plan applies here
              automatically.
            </p>
          </Banner>
        ) : null}

        <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="400">
          {plansQuery.data.map((plan) => {
            const isCurrent = plan.id === currentPlanId;
            const isUpgrade = rank(plan.id) > currentRank;
            return (
              <Card key={plan.id}>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h3" variant="headingMd">
                      {plan.name}
                    </Text>
                    {isCurrent ? <Badge tone="success">Current</Badge> : null}
                  </InlineStack>

                  <Text as="p" variant="headingLg">
                    {plan.price === 0 ? 'Free' : `$${plan.price}/mo`}
                  </Text>

                  <List>
                    <List.Item>{formatLimit(plan.limits.maxCampaigns)} campaigns</List.Item>
                    <List.Item>
                      {formatLimit(plan.limits.monthlyGamePlays)} game plays / mo
                    </List.Item>
                    {plan.features.map((f) => (
                      <List.Item key={f}>{featureLabel(f)}</List.Item>
                    ))}
                  </List>

                  {isCurrent ? (
                    <Button disabled>Current plan</Button>
                  ) : (
                    <Button
                      {...(isUpgrade && !managedPricing ? { variant: 'primary' as const } : {})}
                      {...(managedPricing && pricingUrl
                        ? { url: pricingUrl, target: '_top' as const }
                        : {
                            loading: busyPlan === plan.id,
                            onClick: () => void changePlan(plan, isUpgrade),
                          })}
                    >
                      {managedPricing
                        ? 'Select on Shopify'
                        : isUpgrade
                          ? 'Upgrade'
                          : 'Downgrade'}
                    </Button>
                  )}
                </BlockStack>
              </Card>
            );
          })}
        </InlineGrid>
      </BlockStack>

      {toast ? <Toast content={toast} onDismiss={() => setToast(null)} /> : null}
    </Page>
  );
}
